import { Tables } from '@/lib/supabase/supabase_types_override';

interface ResolveUserRoleBase<U extends Tables<'user'> = Tables<'user'>> {
  user: U;
}

interface ResolveUserProjectRole<U extends Tables<'user'> = Tables<'user'>>
  extends ResolveUserRoleBase<U> {
  projectRbac: Tables<'role_based_project_access_control'>[];
  userProjectRole?: Tables<'user_project_role'>;
}

interface ResolveUserOrgRole<U extends Tables<'user'> = Tables<'user'>>
  extends ResolveUserRoleBase<U> {
  orgRbac: Tables<'role_based_organization_access_control'>[];
  userOrgRole?: Tables<'user_organization_role'>;
}

export function resolveUserProjectRoles<
  U extends Tables<'user'> = Tables<'user'>
>({
  user,
  projectRbac,
  userProjectRole,
}: ResolveUserProjectRole<U>): U & {
  projectRole: Tables<'role_based_project_access_control'> | null;
} {
  return {
    ...user,
    projectRole:
      projectRbac.find(
        (rbac) => rbac.id === userProjectRole?.project_role_id
      ) || null,
  };
}

export function resolveUserOrgRoles<U extends Tables<'user'> = Tables<'user'>>({
  user,
  orgRbac,
  userOrgRole,
}: ResolveUserOrgRole<U>): U & {
  orgRole: Tables<'role_based_organization_access_control'> | null;
} {
  return {
    ...user,
    orgRole:
      orgRbac.find((rbac) => rbac.id === userOrgRole?.organization_role_id) ||
      null,
  };
}

export type ResolveUserRoles = ResolveUserProjectRole & ResolveUserOrgRole;

export type UserWithRoles<U extends Tables<'user'> = Tables<'user'>> = U & {
  projectRole: Tables<'role_based_project_access_control'> | null;
  orgRole: Tables<'role_based_organization_access_control'> | null;
};

export function resolveUserRoles<U extends Tables<'user'> = Tables<'user'>>({
  user,
  projectRbac,
  userProjectRole,
  orgRbac,
  userOrgRole,
}: ResolveUserRoles & { user: U }) : UserWithRoles<U> {
  return {
    ...resolveUserProjectRoles<U>({
      user,
      projectRbac,
      userProjectRole,
    }),
    ...resolveUserOrgRoles<U>({
      user,
      orgRbac,
      userOrgRole,
    }),
  };
}
